import { ElementRef, HostListener, ViewChild, Directive } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar as MatSnackBar } from "@angular/material/snack-bar";
import { Store } from "@ngxs/store";
import { Multisaveable } from "huddyapi";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { ComponentCanDeactivate } from "../guards/save.guard";
import { DesktopErrorHandler } from "../services/errorHandler.service";
import { ConfirmationDialog, ConfirmationDialogResult } from "./confirmation-dialog/confirmation-dialog.component";
import { SearchDialog, SearchDialogData } from "./search-dialog/search-dialog.component";
import { SelfAwarePromise, Util } from "./util";

export abstract class Dependency {
  private static nextId: number = 1;

  private dependencyId: number;
  private _dependencies: Dependency[];
  private subscriptions: Subscription[];
  updates: Subject<Map<number, void>>;

  set dependencies(dependencies: Dependency[]) {
    this._dependencies = dependencies;
    this.connectDependencies();
  }

  get dependencies(): Dependency[] {
    return this._dependencies;
  }

  abstract onUpdate(updatedMap: Map<number, void>): void;

  constructor(dependencies?: Dependency[]) {
    this.dependencyId = Dependency.nextId++;
    this.dependencies = dependencies || [];
    this.updates = new Subject<Map<number, void>>();
  }

  private connectDependencies() {
    if(this.subscriptions)
      this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.subscriptions = this.dependencies.map((dependency: Dependency): Subscription =>
      dependency.updates.subscribe((updateMap: Map<number, void>) => this.update(updateMap)));
  }

  addDependencies(...dependencies: Dependency[]) {
    const existingDependencyIds: Map<number, void> = new Map<number, void>();
    this.dependencies.forEach((dependency: Dependency) => existingDependencyIds.set(dependency.dependencyId));
    const newDependencies: Dependency[] = dependencies.filter((dependency: Dependency): boolean =>
      !existingDependencyIds.has(dependency.dependencyId));
    this._dependencies = this.dependencies.concat(newDependencies);
    this.subscriptions = this.subscriptions.concat(newDependencies.map((dependency: Dependency): Subscription =>
      dependency.updates.subscribe((updateMap: Map<number, void>) => this.update(updateMap))));
  }

  removeDependency(dependency: Dependency) {
    let index: number = this.dependencies.findIndex((d: Dependency): boolean => d.dependencyId == dependency.dependencyId);
    if(index < 0)
      return;
    this.dependencies.splice(index, 1);
    this.subscriptions.splice(index, 1)[0].unsubscribe();
  }

  update(updatedMap: Map<number, void> = new Map<number, void>()): void {
    if(updatedMap.has(this.dependencyId))
      return;
    updatedMap.set(this.dependencyId);
    this.onUpdate(updatedMap);
    this.updates.next(updatedMap);
  }

  getDependencyId(): number {
    return this.dependencyId;
  }
}

export class SimpleDependency extends Dependency {
  constructor(dependencies?: Dependency[]) {
    super(dependencies);
  }

  onUpdate(): void {}
}

export class ValueDependency<T = any> extends Dependency {
  private value: T;

  constructor(value?: T, dependencies?: Dependency[]) {
    super(dependencies)
    this.value = value;
  }

  onUpdate(): void {}

  getValue(): T {
    return this.value;
  }

  setValue(value: T): void {
    this.value = value;
    this.update();
  }
}

export abstract class AbstractSumDependency extends Dependency {
  abstract getSum(): number;
}

export class SumDependency extends AbstractSumDependency {
  private value: number;

  constructor(dependencies?: Dependency[]) {
    super(dependencies);
    this.value = 0;
  }

  onUpdate(): void {}

  add(value: number) {
    this.value += value;
    this.update();
  }

  getSum(): number {
    return this.value;
  }
}

export class SumContributionsDependency<T = number> extends AbstractSumDependency {
  private sum: number;
  private contributions: Map<T, number>;

  constructor(dependencies?: Dependency[]) {
    super(dependencies);
    this.sum = 0;
    this.contributions = new Map<T, number>();
  }

  onUpdate(): void {}

  setContribution(contributionId: T, value: number): void {
    value = value || 0;
    this.sum += value - this.getContribution(contributionId);
    if(value == 0)
      this.contributions.delete(contributionId);
    else
      this.contributions.set(contributionId, value);
    this.update();
  }

  getContribution(contributionId: T): number {
    return this.contributions.get(contributionId) || 0;
  }

  hasContributors(): boolean {
    return this.contributions.size > 0;
  }

  getSum(): number {
    return this.sum;
  }
}

export enum ContributionGroupAggregationType {
  FIRST, SUM
}

type ValueCount = { value: number, count: number };
type ContributionGroup<T> = { type: ContributionGroupAggregationType, contributors: Map<T, ValueCount>, values: ValueCount[] };

export class SumContributionGroupsDependency<T = number, U = number> extends AbstractSumDependency {
  private static DEFAULT_GROUP_AGGREGATION_TYPE: ContributionGroupAggregationType = ContributionGroupAggregationType.FIRST;

  private sum: number;
  private contributions: Map<T, ContributionGroup<U>>;
  private comparator: (a: ValueCount, b: ValueCount) => number;
  private defaultGroupAggregationType: ContributionGroupAggregationType;

  constructor(valueComparator: (a: number, b: number) => number, dependencies?: Dependency[],
    defaultGroupAggregationType: ContributionGroupAggregationType = SumContributionGroupsDependency.DEFAULT_GROUP_AGGREGATION_TYPE) {
    super(dependencies);
    this.comparator = (a: ValueCount, b: ValueCount): number => valueComparator(a.value, b.value);
    this.sum = 0;
    this.contributions = new Map<T, ContributionGroup<U>>();
    this.defaultGroupAggregationType = defaultGroupAggregationType;
  }

  private getContributionFromGroup(group: ContributionGroup<U>): number {
    if(!(group?.values.length > 0))
      return 0;
    switch(group.type) {
      case ContributionGroupAggregationType.FIRST:
        return group.values[0].value;
      case ContributionGroupAggregationType.SUM:
        let sum: number = 0;
        group.values.forEach((value: ValueCount) => sum += value.value * value.count);
        return sum;
      default:
        return 0;
    }
  }

  onUpdate(): void {}

  setGroupType(groupId: T, type: ContributionGroupAggregationType): void {
    let group: ContributionGroup<U> = this.contributions.get(groupId);
    if(!group) {
      if(type == this.defaultGroupAggregationType)
        return;
      this.contributions.set(groupId, {
        type: type,
        contributors: new Map<U, ValueCount>(),
        values: []
      });
      return;
    }
    if(type == this.defaultGroupAggregationType && group.values.length == 0) {
      this.contributions.delete(groupId);
      return;
    }
    if(type == group.type)
      return;
    const oldGroupContribution: number = this.getContributionFromGroup(group);
    group.type = type;
    const groupContribution: number = this.getContributionFromGroup(group);
    if(groupContribution != oldGroupContribution) {
      this.sum += groupContribution - oldGroupContribution;
      this.update();
    }
  }

  setContribution(groupId: T, contributorId: U, value: number): void {
    let group: ContributionGroup<U> = this.contributions.get(groupId);
    if(!group) {
      if(value == null)
        return;
      const valueCount: ValueCount = { value: value, count: 1 };
      this.contributions.set(groupId, {
        type: this.defaultGroupAggregationType,
        contributors: new Map<U, ValueCount>([[contributorId, valueCount]]),
        values: [valueCount]
      });
      this.sum += value;
      this.update();
      return;
    }
    const existing: ValueCount = group.contributors.get(contributorId);
    let oldGroupContribution: number = this.getContributionFromGroup(group);
    if(existing) {
      if(value == existing.value)
        return;
      if(existing.count > 1)
        existing.count--;
      else
        group.values.splice(Util.sortedIndexOf(group.values, existing, this.comparator), 1);
    }
    if(value == null) {
      if(group.values.length == 0 && group.type == this.defaultGroupAggregationType)
        this.contributions.delete(groupId);
      else
        group.contributors.delete(contributorId);
    } else {
      const index: number = Util.sortedIndexOf(group.values, { value: value, count: null }, this.comparator);
      let valueCount: ValueCount;
      if(index < 0) {
        valueCount = { value: value, count: 1 };
        group.values.splice(index * -1 - 1, 0, valueCount);
      } else {
        valueCount = group.values[index];
        valueCount.count++;
      }
      group.contributors.set(contributorId, valueCount);
    }
    const groupContribution: number = this.getContributionFromGroup(group);
    if(groupContribution != oldGroupContribution) {
      this.sum += groupContribution - oldGroupContribution;
      this.update();
    }
  }

  removeGroup(groupId: T): void {
    const oldContribution: number = this.getGroupContribution(groupId);
    if(oldContribution != null) {
      this.contributions.delete(groupId);
      this.sum -= oldContribution;
      this.update();
    }
  }

  getContribution(groupId: T, contributorId: U): number {
    return this.contributions.get(groupId)?.contributors.get(contributorId)?.value || null;
  }

  getGroupContribution(groupId: T): number {
    return this.getContributionFromGroup(this.contributions.get(groupId));
  }

  getSum(): number {
    return this.sum;
  }

  hasContributors(): boolean {
    return this.contributions.size > 0;
  }
}

export class MapCountDependency<T> extends Dependency {
  private map: Map<T, number>;

  constructor() {
    super();
    this.map = new Map<T, number>();
  }

  onUpdate(): void {}

  add(key: T, update: boolean = true): void {
    this.map.set(key, this.count(key) + 1);
    if(update)
      this.update();
  }

  subtract(key: T, update: boolean = true): void {
    const value: number = this.count(key);
    if(value > 0) {
      if(value == 1)
        this.map.delete(key);
      else
        this.map.set(key, value - 1);
      if(update)
        this.update();
    }
  }

  delete(key: T, update: boolean = true): void {
    if(this.map.has(key)) {
      this.map.delete(key);
      if(update)
        this.update();
    }
  }

  replace(oldKey: T, newKey: T, update: boolean = true): void {
    if(this.map.has(oldKey))
      this.subtract(oldKey, false);
    this.add(newKey, update);
  }

  clear(update: boolean = true): void {
    this.map = new Map<T, number>();
    if(update)
      this.update();
  }

  count(key: T): number {
    return this.map.get(key) || 0;
  }
}

export interface Range {
  id: number;
  isValid(): boolean;
  overlaps: (range: Range) => boolean;
}

export interface RangeValue {
  value: number;
  inclusive: boolean;
}

export class LineRange implements Range {
  id: number;
  min: RangeValue;
  max: RangeValue;

  constructor(id: number, min: RangeValue, max: RangeValue) {
    this.id = id;
    this.min = min;
    this.max = max;
  }

  isValid(): boolean {
    return this.min?.value != null && this.max?.value != null && !isNaN(this.min.value) && !isNaN(this.max.value)
      && ((this.min.inclusive && this.max.inclusive) ? this.min.value <= this.max.value : this.min.value < this.max.value);
  }

  overlaps(other: LineRange): boolean {
    if(this.min.value < other.min.value)
      return (this.max.inclusive && other.min.inclusive) ? this.max.value >= other.min.value : this.max.value > other.min.value;
    if(this.min.value > other.min.value)
      return (this.min.inclusive && other.max.inclusive) ? this.min.value <= other.max.value : this.min.value < other.max.value;
    return this.max.value != this.min.value || other.min.inclusive;
  }
}

export class SquareRange implements Range {
  id: number;
  xRange: LineRange;
  yRange: LineRange;

  constructor(id: number, xMin: RangeValue, xMax: RangeValue, yMin: RangeValue, yMax: RangeValue) {
    this.id = id;
    this.xRange = new LineRange(null, xMin, xMax);
    this.yRange = new LineRange(null, yMin, yMax);
  }

  isValid(): boolean {
    return this.xRange.isValid() && this.yRange.isValid();
  }

  overlaps(other: SquareRange): boolean {
    return this.xRange.overlaps(other.xRange) && this.yRange.overlaps(other.yRange);
  }
}

export class RangeOverlapDependency<T extends Range = Range> extends Dependency {
  private ranges: Map<number, { range: T, overlap: Map<number, void> }>;

  constructor(ranges?: T[]) {
    super();
    this.ranges = new Map<number, { range: T, overlap: Map<number, void> }>();
    if(ranges && ranges.length > 0) {
      ranges.forEach((range: T) => this.set(range, false));
      this.update();
    }
  }

  set(range: T, update: boolean = true): void {
    if(this.ranges.has(range.id))
      this.delete(range.id, false);
    if(!range.isValid()) {
      if(update)
        this.update();
      return;
    }
    let rangeData: { range: T, overlap: Map<number, void> } = { range: range, overlap: new Map<number, void>() };
    this.ranges.forEach((otherRangeData: { range: T, overlap: Map<number, void> }) => {
      if(rangeData.range.overlaps(otherRangeData.range)) {
        otherRangeData.overlap.set(range.id);
        rangeData.overlap.set(otherRangeData.range.id);
      }
    });
    this.ranges.set(range.id, rangeData);
    if(update)
      this.update();
  }

  delete(id: number, update: boolean = true): void {
    const rangeData: { range: T, overlap: Map<number, void> } = this.ranges.get(id);
    if(!rangeData)
      return;
    rangeData.overlap.forEach((_, otherId: number) => this.ranges.get(otherId).overlap.delete(id));
    this.ranges.delete(id);
    if(update)
      this.update();
  }

  reset(ranges?: T[], update: boolean = true): void {
    this.ranges = new Map<number, { range: T, overlap: Map<number, void> }>();
    if(ranges)
      ranges.forEach((range: T) => this.set(range, false));
    if(update)
      this.update();
  }

  getOverlapping(id: number): number[] {
    const rangeData: { range: T, overlap: Map<number, void> } = this.ranges.get(id);
    if(!rangeData)
      return [];
    return Array.from(rangeData.overlap.keys());
  }

  onUpdate(): void { };
}

export interface CountUpdators {
  dirty?: (change: number) => void;
  invalid?: (change: number) => void;
  visibleInvalid?: (change: number) => void;
  error?: (change: number) => void;
}

export abstract class SavedField<T = any> extends Dependency {
  private _value: T;
  private _savedValue: T;
  private _error: string;
  private validators: ((field?: SavedField, updatedMap?: Map<number, void>) => string)[];
  private parentUpdators: CountUpdators;
  private touched: boolean;
  dirty: boolean;
  problem: string;

  set value(value: T) {
    const oldValue: T = this._value;
    this._value = value;
    if(this.error)
      this.error = null;
    this.valueChanged(oldValue, value);
    this.update();
  }

  get value(): T {
    return this._value;
  }

  set savedValue(savedValue: T) {
    this._savedValue = savedValue;
    this.update();
  }

  get savedValue(): T {
    return this._savedValue;
  }

  set error(error: string) {
    const hasError: boolean = error != null;
    const shouldUpdateParent: boolean = hasError != (this.error != null);
    this._error = error;
    if(shouldUpdateParent && this.parentUpdators?.error)
      this.parentUpdators.error(hasError ? 1 : -1);
  }

  get error(): string {
    return this._error;
  }

  abstract isDirty(): boolean;
  abstract resetValue(): void;
  abstract isEditable(): boolean;
  abstract valueChanged(oldValue: T, newValue: T): void;

  constructor(savedValue?: T, value?: T, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedField,
    updateMap?: Map<number, void>) => string)[], delaySetup: boolean = false) {
    super(dependencies);
    this._savedValue = savedValue;
    this._value = value != null ? value : savedValue;
    this.parentUpdators = parentUpdators;
    this.touched = false;
    this.validators = validators;
    if(!delaySetup)
      this.doSetup();
  }

  doSetup(): void {
    this.error = null;
    this.dirty = false;
    this.problem = null;
    if(this.value != null)
      this.valueChanged(null, this.value);
    this.update();
  }

  onUpdate(updateMap: Map<number, void>): void {
    const dirty: boolean = this.isDirty();
    const problem: string = this.validate(updateMap);
    if(dirty != this.dirty) {
      this.dirty = dirty;
      if(this.parentUpdators?.dirty)
        this.parentUpdators.dirty(dirty ? 1 : -1);
    }
    const hasProblem: boolean = problem != null;
    if(hasProblem != (this.problem != null)) {
      if(this.parentUpdators?.invalid)
        this.parentUpdators.invalid(hasProblem ? 1 : -1);
      if(this.touched && this.parentUpdators?.visibleInvalid)
        this.parentUpdators.visibleInvalid(hasProblem ? 1 : -1);
    }
    this.problem = problem;
  }

  validate(updateMap: Map<number, void> = new Map<number, void>()): string {
    if(!this.validators)
      return null;
    for(let i = 0; i < this.validators.length; i++) {
      const error: string = this.validators[i](this, updateMap);
      if(error)
        return error;
    }
    return null;
  }

  touch(value: boolean = true): void {
    if(this.touched == value)
      return;
    this.touched = value;
    if(this.parentUpdators?.visibleInvalid && this.problem != null)
      this.parentUpdators.visibleInvalid(value ? 1 : -1);
  }

  reset(): void {
    this.resetValue();
  }

  getDisplayedIssue(): string {
    if(this.error)
      return this.error;
    if(this.problem && this.touched)
      return this.problem;
    return null;
  }
}

export class SimpleSavedField<T> extends SavedField<T> {
  private isDirtyFunction: (field: SimpleSavedField<T>) => boolean;
  private isEditableFunciton: (field: SimpleSavedField<T>) => boolean;
  private resetValueFunction: (field: SimpleSavedField<T>) => void;
  private valueChangedFunction: (oldValue: T, newValue: T, field: SimpleSavedField<T>) => void;

  constructor(savedValue: T, value: T, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SimpleSavedField<T>, updateMap?: Map<number, void>) => string)[],
    isDirty?: (field: SimpleSavedField<T>) => boolean, isEditable?: (field: SimpleSavedField<T>) => boolean,
    resetValue?: (field: SimpleSavedField<T>) => void, valueChanged?: (oldValue: T, newValue: T, field: SimpleSavedField<T>) => void) {
    super(savedValue, value, parentUpdators, dependencies, validators, true);
    this.isDirtyFunction = isDirty || (() => this.value != this.savedValue)
    this.isEditableFunciton = isEditable || (() => false);
    this.resetValueFunction = resetValue || (() => this.savedValue = this.value);
    this.valueChangedFunction = valueChanged;
    this.doSetup();
  }

  isDirty(): boolean {
    return this.isDirtyFunction(this);
  }

  isEditable(): boolean {
    return this.isEditableFunciton(this);
  }

  resetValue(): void {
    this.resetValueFunction(this);
  }

  valueChanged(oldValue: T, newValue: T): void {
    if(this.valueChangedFunction)
      this.valueChangedFunction(oldValue, newValue, this);
  }
}

export class SimpleStatusField extends SavedField<void> {
  private isDirtyFunction: () => boolean;

  constructor(parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SimpleStatusField, updateMap?: Map<number, void>) => string)[],
    isDirty?: () => boolean) {
    super(null, null, parentUpdators, dependencies, validators, true);
    this.isDirtyFunction = isDirty;
    this.doSetup();
  }

  isDirty(): boolean {
    return this.isDirtyFunction ? this.isDirtyFunction() : false;
  }

  isEditable(): boolean {
    return false;
  }

  resetValue(): void {}

  valueChanged(oldValue: void, newValue: void): void {}
}

export class SavedTextField extends SavedField<string> {
  private isEditableFunction: (field?: SavedTextField) => boolean;
  private onValueChanged: (oldValue: string, newValue: string, field: SavedTextField) => void;

  constructor(savedValue?: string, value?: string, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedTextField, updateMap?: Map<number, void>) => string)[],
    isEditable?: (field?: SavedTextField) => boolean, onValueChanged?: (oldValue: string, newValue: string, field: SavedTextField) => void,
    required?: boolean, minChars?: number, maxChars?: number) {
    let commonValidators: ((field?: SavedTextField, updateMap?: Map<number, void>) => string)[] = [];
    if(required)
      commonValidators.push((field: SavedTextField): string => (field.value == null || field.value.length == 0) ? 'Required' : null);
    if(minChars && minChars > 0)
      commonValidators.push((field: SavedTextField): string => (field.value != null && field.value.length < minChars) ? 'Must be at least ' + minChars + ' characters' : null);
    if(maxChars && maxChars > 0)
      commonValidators.push((field: SavedTextField): string => (field.value != null && field.value.length > maxChars) ? 'Can be at most ' + maxChars + ' characters' : null);
    super(savedValue, value, parentUpdators, dependencies, commonValidators.concat(validators || []), true);
    this.isEditableFunction = isEditable || (() => true);
    this.onValueChanged = onValueChanged;
    this.doSetup();
  }

  isDirty(): boolean {
    return (this.value == null ? '' : this.value) != (this.savedValue == null ? '' : this.savedValue);
  }

  resetValue(): void {
    this.value = this.savedValue;
  }

  isEditable(): boolean {
    return this.isEditableFunction(this);
  }

  valueChanged(oldValue: string, newValue: string): void {
    if(this.onValueChanged)
      this.onValueChanged(oldValue, newValue, this);
  }

  touchOrOption(event: any): void {
    if(!(event?.relatedTarget?.nodeName == 'MAT-OPTION'))
      this.touch();
  }
}

export class SavedNumberField extends SavedField<string> {
  private isEditableFunction: (field?: SavedNumberField) => boolean;
  private onValueChanged: (oldValue: string, newValue: string, field: SavedNumberField) => void;
  private int: boolean;

  constructor(savedValue?: string, value?: string, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedNumberField, updateMap?: Map<number, void>) => string)[],
    isEditable?: (field?: SavedNumberField) => boolean, onValueChanged?: (oldValue: string, newValue: string, field: SavedNumberField) => void,
    required?: boolean, min?: number, max?: number, int?: boolean) {
    let commonValidators: ((field?: SavedNumberField, updateMap?: Map<number, void>) => string)[] = [];
    if(required)
      commonValidators.push((field: SavedNumberField): string => (field.value == null || field.value.length == 0) ? 'Required' : null);
    commonValidators.push(int ? (field: SavedNumberField): string => {
      return (field.value == null || /^(|(-?[0-9]+))$/g.test(field.value)) ? '' : 'Must be an integer';
    } : (field: SavedNumberField): string => {
      return (field.value == null || /^(|(-?(([0-9]+(.[0-9]*)?)|([0-9]*.[0-9]+))))$/g.test(field.value)) ? '' : 'Must be a number';
    });
    if(min != null)
      commonValidators.push((field: SavedNumberField): string => (field.value != null && (int ? parseInt : parseFloat)(field.value) < min) ? ('Must be at least ' + min) : null);
    if(max != null)
      commonValidators.push((field: SavedNumberField): string => (field.value != null && (int ? parseInt : parseFloat)(field.value) > max) ? ('Can be at most ' + max) : null);
    super(savedValue, value, parentUpdators, dependencies, commonValidators.concat(validators || []), true);
    this.int = int;
    this.isEditableFunction = isEditable || (() => true);
    this.onValueChanged = onValueChanged;
    this.doSetup();
  }

  isDirty(): boolean {
    return ((this.value == null || this.value.length == 0) ? null : ((this.int ? parseInt : parseFloat)(this.value)))
      != ((this.savedValue == null || this.savedValue.length == 0) ? null : ((this.int ? parseInt : parseFloat)(this.savedValue)));
  }

  resetValue(): void {
    this.value = this.savedValue;
  }

  isEditable(): boolean {
    return this.isEditableFunction(this);
  }

  valueChanged(oldValue: string, newValue: string): void {
    if(this.onValueChanged)
      this.onValueChanged(oldValue, newValue, this);
  }

  isInt(): boolean {
    return this.int;
  }
}

export interface SelectOption<T = any> {
  value: T;
  displayValue: string;
}

export type SelectOptionsChange<T> = { options: SelectOption<T>[], lastOptions: SelectOption<T>[], added: SelectOption<T>[], removed: SelectOption<T>[] };

export class SelectOptions<T = any> {
  private _options: SelectOption<T>[];
  private displayValueMap: Map<T, string>;
  private comparator: (a: SelectOption<T>, b: SelectOption<T>) => number;
  optionsChanged: BehaviorSubject<SelectOptionsChange<T>>;

  set options(options: SelectOption<T>[]) {
    const lastOptions: SelectOption<T>[] = this.options || [];
    this._options = (options || []).sort(this.comparator);
    this.displayValueMap = new Map<T, string>(this.options.map((option: SelectOption<T>) => [option.value, option.displayValue]));
    const lastMap: Map<T, void> = new Map<T, void>(lastOptions.map((option: SelectOption<T>) => [option.value, null]));
    const currentMap: Map<T, void> = new Map<T, void>(this.options.map((option: SelectOption<T>) => [option.value, null]));
    this.optionsChanged.next({
      options: this.options,
      lastOptions: lastOptions,
      added: this.options.filter((option: SelectOption<T>): boolean => !lastMap.has(option.value)),
      removed: lastOptions.filter((option: SelectOption<T>): boolean => !currentMap.has(option.value))
    });
  }

  get options(): SelectOption<T>[] {
    return this._options;
  }

  constructor(options?: SelectOption<T>[], comparator?: (a: SelectOption<T>, b: SelectOption<T>) => number) {
    this.comparator = comparator || ((a: SelectOption<T>, b: SelectOption<T>): number => 
      a.displayValue.toLocaleLowerCase().localeCompare(b.displayValue.toLocaleLowerCase()));
    this.optionsChanged = new BehaviorSubject<SelectOptionsChange<T>>({
      options: [],
      lastOptions: [],
      added: [],
      removed: []
    });
    this.options = options;
  }

  getDisplayValue(value: T): string {
    return this.displayValueMap.get(value);
  }
}

export class SavedSelectField<T = any> extends SavedField<T> {
  private isEditableFunction: (field?: SavedSelectField<T>) => boolean;
  private onValueChanged: (oldValue: T, newValue: T, field: SavedSelectField<T>) => void;
  options: SelectOptions<T>;
  optionValuesEqual: (a: T, b: T) => boolean;
  setValueFromSaved: (savedValue: T) => T;

  constructor(savedValue?: T, value?: T, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedSelectField, updateMap?: Map<number, void>) => string)[], required?: boolean,
    optionValuesEqual?: (a: T, b: T) => boolean, setValueFromSaved?: (savedValue: T) => T, options?: SelectOptions<T>,
    isEditable?: (field?: SavedSelectField<T>) => boolean, onValueChanged?: (oldValue: T, newValue: T, field: SavedSelectField<T>) => void) {
    let commonValidators: ((field?: SavedSelectField<T>, updateMap?: Map<number, void>) => string)[] = [];
    if(required)
      commonValidators.push((field: SavedSelectField<T>): string => (field.value == null) ? 'Required' : null);
    super(savedValue, value, parentUpdators, dependencies, commonValidators.concat(validators || []), true);
    this.isEditableFunction = isEditable || (() => true);
    this.onValueChanged = onValueChanged;
    this.optionValuesEqual = optionValuesEqual || ((a: T, b: T): boolean => a == b);
    this.setValueFromSaved = setValueFromSaved || ((savedValue: T): T => savedValue);
    this.options = options || (new SelectOptions());
    this.doSetup();
  }

  isDirty(): boolean {
    return !this.optionValuesEqual(this.value, this.savedValue);
  }

  resetValue(): void {
    this.value = this.setValueFromSaved(this.savedValue);
  }

  isEditable(): boolean {
    return this.isEditableFunction(this);
  }

  valueChanged(oldValue: T, newValue: T): void {
    if(this.onValueChanged)
      this.onValueChanged(oldValue, newValue, this);
  }

  handleBlur(event: any): void {
    if(!(event?.relatedTarget?.nodeName == 'MAT-OPTION'))
      this.touch();
  }

  openedChange(opened: boolean): void {
    if(!opened)
      this.touch();
  }

  getDisplayValue(): string {
    if(this.value == null)
      return null;
    return this.options.getDisplayValue(this.value);
  }
}

export class SavedMultiselectField<T = any> extends SavedField<T[]> {
  private isEditableFunction: (field?: SavedMultiselectField<T>) => boolean;
  private onValueChanged: (oldValue: T[], newValue: T[], field: SavedMultiselectField<T>, selected: T[], unselected: T[]) => void;
  options: SelectOptions<T>;
  optionValuesEqual: (a: T, b: T) => boolean;
  setValueFromSaved: (savedValue: T[]) => T[];

  constructor(savedValue?: T[], value?: T[], parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedMultiselectField<T>, updateMap?: Map<number, void>) => string)[], required?: boolean,
    optionValuesEqual?: (a: T, b: T) => boolean, setValueFromSaved?: (savedValue: T[]) => T[], options?: SelectOptions<T>,
    isEditable?: (field?: SavedMultiselectField<T>) => boolean, onValueChanged?: (oldValue: T[], newValue: T[], field: SavedMultiselectField<T>, selected: T[], unselected: T[]) => void) {
    let commonValidators: ((field?: SavedMultiselectField<T>, updateMap?: Map<number, void>) => string)[] = [];
    if(required)
      commonValidators.push((field: SavedMultiselectField<T>): string => (field.value?.length > 0) ? null : 'Required');
    super(savedValue, value, parentUpdators, dependencies, commonValidators.concat(validators || []), true);
    this.isEditableFunction = isEditable || (() => true);
    this.onValueChanged = onValueChanged;
    this.optionValuesEqual = optionValuesEqual || ((a: T, b: T): boolean => a == b);
    this.setValueFromSaved = setValueFromSaved || ((savedValue: T[]): T[] => savedValue);
    this.options = options || (new SelectOptions());
    this.doSetup();
  }

  isDirty(): boolean {
    return !Util.arraysEqual(this.value, this.savedValue, this.optionValuesEqual);
  }

  resetValue(): void {
    this.value = this.setValueFromSaved(this.savedValue);
  }

  isEditable(): boolean {
    return this.isEditableFunction(this);
  }

  valueChanged(oldValue: T[], newValue: T[]): void {
    if(this.onValueChanged) {
      const oldMap: Map<T, void> = new Map<T, void>((oldValue || []).map((value: T) => [value, null])),
            newMap: Map<T, void> = new Map<T, void>((newValue || []).map((value: T) => [value, null]));
      this.onValueChanged(oldValue, newValue, this, (newValue || []).filter((value: T): boolean => !oldMap.has(value)),
        (oldValue || []).filter((value: T): boolean => !newMap.has(value)));
    }
  }

  handleBlur(event: any): void {
    if(!(event?.relatedTarget?.nodeName == 'MAT-OPTION'))
      this.touch();
  }

  openedChange(opened: boolean): void {
    if(!opened)
      this.touch();
  }

  getDisplayValue(): string {
    if(this.value == null)
      return null;
    return this.value.map((value: T): string => this.options.getDisplayValue(value)).join(', ');
  }
}

export class SavedSearchableField<T = any> extends SavedTextField {
  private dialog: MatDialog;
  private getDialogData: (field: SavedSearchableField<T>) => SearchDialogData<T>;
  private handleSelection: (selection: T) => void;

  constructor(dialog: MatDialog, getDialogData: (field: SavedSearchableField<T>) => SearchDialogData<T>,
    handleSelection?: (selection: T) => void, savedValue?: string, value?: string, parentUpdators?: CountUpdators,
    dependencies?: Dependency[], validators?: ((field?: SavedTextField, updateMap?: Map<number, void>) => string)[],
    isEditable?: (field?: SavedTextField) => boolean, onValueChanged?: (oldValue: string, newValue: string, field: SavedSearchableField<T>) => void,
    required?: boolean, minChars?: number, maxChars?: number) {
    super(savedValue, value, parentUpdators, dependencies, validators, isEditable, onValueChanged, required, minChars, maxChars);
    this.dialog = dialog;
    this.getDialogData = getDialogData;
    this.handleSelection = handleSelection;
  }

  handleBlur(event: any): void {
    const target: any = event?.relatedTarget;
    if(!(target && (target.name == 'search' || (target.nodeName == 'MAT-DIALOG-CONTAINER' && target.childNodes[0]?.nodeName == 'SEARCH-DIALOG'))))
      this.touch();
  }

  openSearch(): void {
    const data: SearchDialogData<T> = this.getDialogData(this);
    this.dialog.open(SearchDialog, {
      width: '636px',
      data: data
    }).beforeClosed().subscribe((result: T) => {
      this.touch();
      if(!result)
        return;
      this.value = data.getText(result);
      if(this.handleSelection)
        this.handleSelection(result);
    });
  }
}

export class SavedAutocompleteField<T = any> extends SavedTextField {
  autocompleteOptions: SelectOption<T>[];
  loading: boolean;
  private lastTerm: string;
  private searchNumber: number;
  private selecting: boolean;
  private timeout: any;
  private getResults: (term: string) => T[] | Promise<T[]>;
  private getNameFromResult: (result: T) => string;
  private handleSelectionFunction: (value: T) => void;

  constructor(getResults: (term: string) => T[] | Promise<T[]>, getNameFromResult: (result: T) => string, handleSelection: (value: T) => void,
    savedValue?: string, value?: string, parentUpdators?: CountUpdators, dependencies?: Dependency[],
    validators?: ((field?: SavedTextField, updateMap?: Map<number, void>) => string)[], isEditable?: (field?: SavedTextField) => boolean,
    onValueChanged?: (oldValue: string, newValue: string, field: SavedSearchableField<T>) => void, required?: boolean, minChars?: number, maxChars?: number) {
    super(savedValue, value, parentUpdators, dependencies, validators, isEditable, onValueChanged, required, minChars, maxChars);
    this.getResults = getResults;
    this.getNameFromResult = getNameFromResult;
    this.handleSelectionFunction = handleSelection;
    this.searchNumber = 0;
    this.selecting = false;
    this.lastTerm = '';
    this.updates.subscribe(() => {
      let term: string = (this.value || '').trim();
      if(term.length < 3)
        term = '';
      if(term != this.lastTerm) {
        this.lastTerm = term;
        this.searchInputChanged(term);
      }
    });
  }

  handleBlur(event: any): void {
    if(!(event?.relatedTarget?.nodeName == 'MAT-OPTION'))
      this.touch();
  }

  handleSelection(option: SelectOption<T>) {
    this.touch();
    this.searchInputChanged('');
    this.handleSelectionFunction(option.value);
  }

  private searchInputChanged(term: string = '') {
    clearTimeout(this.timeout);
    if(term == '') {
      this.searchNumber++;
      this.autocompleteOptions = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    this.timeout = setTimeout(() => {
      const searchNumber = ++this.searchNumber;
      Promise.resolve(this.getResults(term)).then((results: T[]) => {
        if(this.searchNumber != searchNumber)
          return;
        this.autocompleteOptions = results.map((result: T): SelectOption<T> => {
          return { value: result, displayValue: this.getNameFromResult(result) };
        }).sort((a: SelectOption, b: SelectOption<T>): number => (a.displayValue || '').trim().toLocaleLowerCase().localeCompare((b.displayValue || '').trim().toLocaleLowerCase()));
        this.loading = false;
      }).catch(() => {
        if(this.searchNumber != searchNumber)
          return;
        this.autocompleteOptions = [];
        this.loading = false;
      });
    }, 300);
  }
}

export class SavedGroupStatus {
  private lastDirty: boolean;
  private lastInvalid: boolean;
  private lastVisiblyInvalid: boolean;
  private lastError: boolean;
  private _dirty: number;
  private _invalid: number;
  private _visibleInvalid: number;
  private _error: number;
  parentUpdators: CountUpdators;
  updators: CountUpdators;
  afterDetach: () => void;

  set dirty(dirty: number) {
    this._dirty = dirty;
    this.updateDirty();
  }

  get dirty(): number {
    return this._dirty;
  }

  set invalid(invalid: number) {
    this._invalid = invalid;
    this.updateInvalid();
  }

  get invalid(): number {
    return this._invalid;
  }

  set visibleInvalid(visibleInvalid: number) {
    this._visibleInvalid = visibleInvalid;
    this.updateVisibleInvalid();
  }

  get visibleInvalid(): number {
    return this._visibleInvalid;
  }

  set error(error: number) {
    this._error = error;
    this.updateError();
  }

  get error(): number {
    return this._error;
  }

  constructor(parentUpdators: CountUpdators, afterDetach?: () => void) {
    this.lastDirty = false;
    this.lastInvalid = false;
    this.lastVisiblyInvalid = false;
    this.lastError = false;
    this._dirty = 0;
    this._invalid = 0;
    this._visibleInvalid = 0;
    this._error = 0;
    this.parentUpdators = parentUpdators;
    this.afterDetach = afterDetach;
    this.updators = {
      dirty: (change: number) => this.dirty += change,
      invalid: (change: number) => this.invalid += change,
      visibleInvalid: (change: number) => this.visibleInvalid += change,
      error: (change: number) => this.error += change
    };
  }

  updateDirty(): void {
    const isDirty: boolean = this.isDirty();
    if(isDirty != this.lastDirty && this.parentUpdators?.dirty) {
      this.lastDirty = isDirty;
      this.parentUpdators.dirty(isDirty ? 1 : -1);
    }
  }

  updateInvalid(): void {
    const isInvalid: boolean = this.isInvalid();
    if(isInvalid != this.lastInvalid && this.parentUpdators?.invalid) {
      this.lastInvalid = isInvalid;
      this.parentUpdators.invalid(isInvalid ? 1 : -1);
    }
  }

  updateVisibleInvalid(): void {
    const isVisiblyInvalid: boolean = this.isVisiblyInvalid();
    if(isVisiblyInvalid != this.lastVisiblyInvalid && this.parentUpdators?.visibleInvalid) {
      this.lastVisiblyInvalid = isVisiblyInvalid;
      this.parentUpdators.visibleInvalid(isVisiblyInvalid ? 1 : -1);
    }
  }

  updateError(): void {
    const hasError: boolean = this.hasError();
    if(hasError != this.lastError && this.parentUpdators?.error) {
      this.lastError = hasError;
      this.parentUpdators.error(hasError ? 1 : -1);
    }
  }

  detach(): void {
    const parentUpdators: CountUpdators = this.parentUpdators;
    this.parentUpdators = null;
    if(this.isDirty() && parentUpdators?.dirty)
      parentUpdators.dirty(-1);
    if(this.isInvalid() && parentUpdators?.invalid)
      parentUpdators.invalid(-1);
    if(this.isVisiblyInvalid() && parentUpdators?.visibleInvalid)
      parentUpdators.visibleInvalid(-1);
    if(this.hasError() && parentUpdators?.error)
      parentUpdators.error(-1);
    if(this.afterDetach)
      this.afterDetach();
  }

  isDirty(): boolean {
    return this.dirty > 0;
  }

  isInvalid(): boolean {
    return this.invalid > 0;
  }

  isVisiblyInvalid(): boolean {
    return this.visibleInvalid > 0;
  }

  hasError(): boolean {
    return this.error > 0;
  }

  hasVisibleIssue(): boolean {
    return this.isVisiblyInvalid() || this.hasError();
  }
}

export abstract class SavedObject<T extends Multisaveable> extends SavedGroupStatus {
  static nextId: number = -1;

  private allowInvalid: boolean;
  private saveOperation: SelfAwarePromise<boolean>;
  private deleting: boolean;
  private _groupIndex: number;
  id: number;
  groups: SavedGroupStatus[];
  dialog: MatDialog;
  abstract objectName: string;
  public from : any;

  set groupIndex(groupIndex: number) {
    this.beforeGroupChanged();
    this._groupIndex = groupIndex;
    this.onGroupChanged();
  }

  get groupIndex(): number {
    return this._groupIndex;
  }

  abstract getSavedName(): string;
  abstract onDetach(): void;
  abstract onUpdateFrom(from: T, wasNew?: boolean): void;
  abstract touchAll(value: boolean): void;
  abstract warnValidation(): void;
  abstract beforeGroupChanged(): void;
  abstract onGroupChanged(): void;
  abstract onReset(): void;
  abstract onSave(displayErrorIfPresent?: boolean): Promise<boolean>;
  abstract onDeleteExisting(displayErrorIfPresent?: boolean): Promise<boolean>;
  abstract onDeleteNew(): void;

  constructor(object: any, allowInvalid?: boolean, groupCount?: number, parentUpdators?: CountUpdators, dialog?: MatDialog) {
    super(parentUpdators);
    this.from = object;
    this.allowInvalid = !!allowInvalid;
    this.afterDetach = (): void => this.onDetach();
    this.dialog = dialog;
    this.groups = [];
    for(let i = 0; i < groupCount; i++)
      this.groups.push(new SavedGroupStatus(this.updators));
    if(groupCount && groupCount > 0)
      this._groupIndex = 0;
    this.id = object?.id || SavedObject.nextId--;
    this.updateDirty();
  }

  isNew(): boolean {
    return this.id < 0;
  }

  isDirty(): boolean {
    return this.dirty > 0 || this.isNew();
  }

  isSaving(): boolean {
    return this.saveOperation && this.saveOperation.isPending();
  }

  canReset(): boolean {
    return this.isDirty() && !this.isSaving()
  }

  discardEnabled(): boolean {
    return this.canReset() && !this.isNew();
  }

  saveEnabled(): boolean {
    return !(this.isSaving() || (this.allowInvalid ? this.hasError() : this.hasVisibleIssue())) && this.isDirty();
  }

  canSave(): boolean {
    return this.saveEnabled() && (this.allowInvalid || !this.isInvalid());
  }

  deleteEnabled(): boolean {
    return !this.isSaving();
  }

  updateFrom(from: T): void {
    const wasNew: boolean = this.isNew();
    this.id = from ? from.id : SavedObject.nextId--;
    this.onUpdateFrom(from, wasNew);
    if(this.isNew() != wasNew)
      this.updateDirty();
    if(!this.hasVisibleIssue())
      this.touchAll(false);
  }

  getListingName(): string {
    return this.getSavedName() || ('New ' + this.objectName);
  }

  getPromise(): Promise<boolean> {
    return this.saveOperation ? this.saveOperation.getPromise() : Promise.resolve(true);
  }

  isDeleting(): boolean {
    return this.deleting;
  }

  reset(confirm: boolean = true): Promise<boolean> {
    if(!this.canReset())
      return Promise.resolve(false);
    if(!(confirm && this.dialog)) {
      this.onReset();
      return Promise.resolve(true);
    }
    let dialogRef: MatDialogRef<ConfirmationDialog> = this.dialog.open(ConfirmationDialog, {
      width: '636px',
      data: {
        minWidth: '182px',
        title: 'Discard changes?',
        body: 'Are you sure that you want to discard all of your unsaved changes to \'' + this.getListingName() + '\'?',
        buttons: [
          {
            text: 'Cancel',
            class: 'secondary',
            handler: (): boolean => {
              return false;
            }
          },
          {
            text: 'Discard changes',
            class: '',
            handler: (): boolean => {
              this.onReset();
              return true;
            }
          }
        ]
      }
    });
    return new Promise<boolean>(resolve => {
      dialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => resolve(result?.result || false));
    });
  }

  async save(displayErrorIfPresent: boolean = true): Promise<boolean> {
    if(this.isSaving())
      return false;
    if(!this.isDirty())
      return true;
    if(this.allowInvalid ? this.hasError() : this.hasVisibleIssue())
      return false;
    if(this.canSave()) {
      this.saveOperation = SelfAwarePromise.fromPromise(this.onSave(displayErrorIfPresent));
      return this.getPromise();
    } else {
      this.touchAll(true);
      if(displayErrorIfPresent)
        this.warnValidation();
      return false;
    }
  }

  async delete(displayErrorIfPresent: boolean = true, confirm: boolean = true): Promise<boolean> {
    if(this.isSaving())
      return false;
    if(!(confirm && this.dialog)) {
      if(this.isNew()) {
        this.onDeleteNew();
        return true;
      } else {
        this.deleting = true;
        this.saveOperation = SelfAwarePromise.fromPromise(this.onDeleteExisting(displayErrorIfPresent));
        return this.getPromise().finally(() => this.deleting = false);
      }
    }
    const name: string = this.getListingName();
    return new Promise<boolean>((resolve) => {
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        width: '636px',
        data: {
          minWidth: '115px',
          title: 'Delete \'' + name + '\'?',
          body: 'Are you sure that you want to permanently delete \'' + name + '\'?  This action cannot be undone.',
          buttons: [
            {
              text: 'Cancel',
              class: 'secondary',
              handler: (): boolean => {
                resolve(false);
                return false;
              }
            },
            {
              text: 'Delete',
              class: '',
              handler: (): boolean | Promise<boolean> => {
                if(this.isNew()) {
                  this.onDeleteNew();
                  resolve(true);
                  return true;
                }
                this.deleting = true;
                this.saveOperation = SelfAwarePromise.fromPromise(this.onDeleteExisting(displayErrorIfPresent));
                return this.getPromise().then((value: boolean) => {
                  this.deleting = false;
                  resolve(value);
                  return value;
                });
              }
            }
          ]
        }
      });
      dialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
        if(!result)
          resolve(false);
      });
    });
  }
}

@Directive()
export abstract class SimpleMultisavingPage<T extends SavedObject<U>, U extends Multisaveable> extends SavedGroupStatus implements ComponentCanDeactivate {
  @ViewChild('fieldInfo', { static: false }) fieldInfo: ElementRef;
  @ViewChild('autofocus', { static: false }) set autofocusInput(autofocusInput: ElementRef) {
    this._autofocusInput = autofocusInput;
    if(this.shouldAutofocus && autofocusInput != null) {
      this.shouldAutofocus = false;
      autofocusInput.nativeElement.focus();
    }
  }

  get autofocusInput(): ElementRef {
    return this._autofocusInput;
  }

  savedObjectMap: Map<number, T>;
  savedObjects: T[];
  _selectedId: number;
  showingMissingSelectedDialog: number;
  missingDialogRef: MatDialogRef<ConfirmationDialog>;
  shouldAutofocus: boolean;
  _autofocusInput: ElementRef;
  fieldInfoShowing: boolean;
  fieldInfoElem: HTMLElement;
  fieldInfoPosition: string;
  fieldInfoArrowPosition: string;

  set selectedId(selectedId: number) {
    this._selectedId = selectedId;
    this.fieldInfoElem = null;
    this.selectionChanged();
  }

  get selectedId(): number {
    return this._selectedId;
  }

  abstract afterContentChecked(): void;
  abstract constructNewSavedObject(from?: U): T;
  abstract compareSavedObjects(a: T, b: T): number;
  abstract ignoreKeyboardArrowEvent(event: any): boolean;
  abstract selectionChanged(): void;

  constructor(
    public singularName: string,
    public pluralName: string,
    public errorHandler: DesktopErrorHandler,
    public store: Store,
    public snackbar: MatSnackBar,
    public dialog: MatDialog
  ) {
    super(null);
    this.selectedId = null;
    this.showingMissingSelectedDialog = null;
    this.missingDialogRef = null;
    this.shouldAutofocus = false;
    this.fieldInfoShowing = false;
    this.savedObjects = [];
    this.savedObjectMap = new Map<number, T>();
  }

  ngAfterContentChecked(): void {
    if(this.fieldInfoShowing && this.fieldInfoElem) {
      this.fieldInfoPosition = this.getFieldInfoPosition();
      this.fieldInfoArrowPosition = this.getFieldInfoArrowPosition();
    }
    this.afterContentChecked();
  }

  updateObjectListAndMap(objects: U[]): void {
    
    let presentMap: Map<number, void> = new Map<number, void>();
    objects.forEach((object: U) => {
      presentMap.set(object.id);
      let savedObject: T;
      if(object.createdFrom != null && this.savedObjectMap.has(object.createdFrom)) {
        savedObject = this.savedObjectMap.get(object.createdFrom);
        const wasSelected: boolean = this.isSelected(savedObject);
        savedObject.updateFrom(object);
        this.savedObjectMap.set(object.id, savedObject);
        if(wasSelected)
          this.select(savedObject, true);
        this.savedObjectMap.delete(object.createdFrom);
        presentMap.set(object.createdFrom);
      } else if(this.savedObjectMap.has(object.id)) {
        if(this.showingMissingSelectedDialog == object.id)
          this.missingDialogRef?.close();
        savedObject = this.savedObjectMap.get(object.id);
        savedObject.updateFrom(object);
      } else {
        savedObject = this.constructNewSavedObject(object);
        this.savedObjectMap.set(object.id, savedObject);
      }
      return savedObject;
    });
    const deleted: T[] = this.savedObjects.filter((savedObject: T): boolean => !(savedObject.isNew() || presentMap.has(savedObject.id)));
    if(deleted.length > 0)
      this.removeOrUnbackSavedObjects(deleted);
    else
      this.setSavedObjectsFromMap();
  }

  canDeactivate(): boolean | Promise<boolean> {
    let hasExistingSaveOperations: boolean = this.savedObjects.find((savedObject: T): boolean => savedObject.isSaving()) != null;
    if(!(this.isDirty() || hasExistingSaveOperations))
      return true;
    return new Promise<boolean>((resolve) => {
      this.missingDialogRef?.close();
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        width: '636px',
        data: {
          minWidth: '300px',
          title: 'Leave without saving?',
          body: 'Any unsaved changes will be lost forever.',
          buttons: [
            {
              text: 'Cancel',
              class: 'secondary',
              handler: (): void => {
                resolve(false);
              }
            },
            {
              text: 'Discard changes',
              class: 'danger',
              handler: (): void => {
                resolve(true);
              }
            },
            {
              text: 'Save changes',
              class: '',
              disabled: (): boolean => this.hasVisibleIssue(),
              handler: async (): Promise<void> => {
                return this.saveAll().then((value: boolean) => {
                  resolve(value);
                });
              }
            }
          ],
          init: async (): Promise<boolean> => {
            const existingSaveOperations: Promise<boolean>[] = this.savedObjects.filter((savedObject: T) =>
              savedObject.isSaving()).map((savedObject: T): Promise<boolean> => savedObject.getPromise());
            return Promise.all(existingSaveOperations).then((values: boolean[]) => {
              if(values.find((value: boolean): boolean => !value) != null) {
                resolve(false);
                return false;
              }
              if(this.isDirty() || this.isInvalid() || this.hasError())
                return true;
              resolve(true);
              return false;
            });
          }
        }
      });
      dialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
        if(!result)
          resolve(false);
      });
    });
  }

  async saveAll(): Promise<boolean> {
    return Promise.all(this.savedObjects.filter((savedObject: T): boolean => !savedObject.isSaving()).map((savedObject: T): Promise<boolean> => savedObject.save(false))).then((values: boolean[]) => {
      if(values.find((value: boolean): boolean => !value) == null)
        return true;
      this.snackbar.open('Error saving ' + this.pluralName, 'Dismiss', {
        duration: 3000
      });
      return false;
    });
  }

  select(object: T | U, forCreate: boolean = false): void {
    this.selectedId = object?.id;
    if(object && !forCreate)
      this.shouldAutofocus = true;
    
  }

  isSelected(object: T | U): boolean {
    return this.selectedId == object.id;
  }

  getSelected(): T {
    return this.selectedId != null ? this.savedObjectMap.get(this.selectedId) : null;
  }

  addSavedObject(): void {
    const savedObject: T = this.constructNewSavedObject();
    this.savedObjectMap.set(savedObject.id, savedObject);
    this.savedObjects.splice(Util.getSortedIndex(this.savedObjects, savedObject, this.compareSavedObjects), 0, savedObject);
    this.select(savedObject);
  }

  removeSavedObjects(savedObjects: T[]): void {
    let selectedIndex: number;
    savedObjects.forEach((savedObject: T) => {
      if(this.showingMissingSelectedDialog == savedObject.id)
        this.missingDialogRef?.close();
      if(this.isSelected(savedObject)) {
        selectedIndex = this.getSelectedIndex();
        this.select(null);
      }
      this.savedObjectMap.delete(savedObject.id);
      savedObject.detach();
    });
    this.setSavedObjectsFromMap(selectedIndex);
  }

  removeOrUnbackSavedObjects(savedObjects: T[]): void {
    let selectedIndex: number;
    let deleted: boolean = false;
    savedObjects.forEach((savedObject: T) => {
      if(this.showingMissingSelectedDialog == savedObject.id)
        return;
      const isSelected: boolean = this.isSelected(savedObject);
      if(isSelected && !savedObject.isDeleting() && this.showingMissingSelectedDialog == null) {
        this.dialog.closeAll();
        this.showingMissingSelectedDialog = savedObject.id;
        this.missingDialogRef = this.dialog.open(ConfirmationDialog, {
          width: '636px',
          data: {
            minWidth: '300px',
            title: 'This ' + this.singularName + ' no longer exists',
            body: savedObject.getListingName() + ' seems to have been deleted.  Would you like to continue editing it?',
            buttons: [
              {
                text: 'Continue editing',
                class: 'secondary',
                handler: (): boolean => {
                  const oldId: number = savedObject.id;
                  savedObject.updateFrom(null);
                  this.savedObjectMap.set(savedObject.id, savedObject);
                  this.savedObjectMap.delete(oldId);
                  if(isSelected)
                    this.select(savedObject, true);
                  this.setSavedObjectsFromMap();
                  return true;
                }
              },
              {
                text: 'Remove',
                class: '',
                handler: (): boolean => false
              }
            ]
          }
        });
        this.missingDialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
          this.showingMissingSelectedDialog = null;
          this.missingDialogRef = null;
          if(result?.result || result?.error)
            return;
          this.removeSavedObjects([savedObject]);
        });
        return;
      }
      if(savedObject.isDirty()) {
        const oldId: number = savedObject.id;
        savedObject.updateFrom(null);
        this.savedObjectMap.set(savedObject.id, savedObject);
        this.savedObjectMap.delete(oldId);
        if(isSelected)
          this.select(savedObject, true);
      } else {
        if(isSelected) {
          selectedIndex = this.getSelectedIndex();
          this.select(null);
        }
        this.savedObjectMap.delete(savedObject.id);
        savedObject.detach();
        if(!deleted)
          deleted = true;
      }
    });
    if(deleted)
      this.setSavedObjectsFromMap(selectedIndex);
  }

  setSavedObjectsFromMap(selectIndex: number = 0): void {
    this.savedObjects = Array.from(this.savedObjectMap.values()).sort(this.compareSavedObjects);
    if(this.selectedId == null && this.savedObjects.length > 0)
      this.select(this.savedObjects[Math.max(0, Math.min(this.savedObjects.length - 1, selectIndex))])
  }

  getSelectedIndex(): number {
    let result: number = -1;
    if(this.selectedId) {
      this.savedObjects.find((savedObject: T, index: number): boolean => {
        if(this.isSelected(savedObject)) {
          result = index;
          return true;
        }
        return false;
      });
    }
    return result;
  }

  selectNext(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    this.select(this.savedObjects[(index + 1) % this.savedObjects.length]);
  }

  selectPrevious(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    this.select(this.savedObjects[index == 0 ? this.savedObjects.length - 1 : index - 1]);
  }

  getEmptyStateTitle(): string {
    return this.savedObjects.length == 0 ? 'No ' + this.pluralName : 'No ' + this.singularName + ' selected';
  }

  getEmptyStateDesc(): string {
    return this.savedObjects.length == 0 ? 'Add a ' + this.singularName + ' to begin' : 'Add or select a ' + this.singularName + ' to begin';
  }

  getFieldInfoPositionInfo(): { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } {
    if(!(this.fieldInfoElem && this.fieldInfo && this.fieldInfo.nativeElement))
      return null;
    let vMode: number = 0; //-1 = above, 0 = side, 1 = below
    const position = this.fieldInfoElem.getBoundingClientRect();
    if(position.y <= 17)
      vMode = 1;
    else if(position.bottom + 17 >= window.innerHeight)
      vMode = -1;
    const popoverSpace: number = this.fieldInfo.nativeElement.offsetWidth + 20;
    let leftSpace: boolean = position.left - popoverSpace > 0, rightSpace: boolean = position.right + popoverSpace < window.innerWidth;
    if(vMode == 0 && !(leftSpace || rightSpace))
      vMode = (position.top + position.height / 2 > window.innerHeight / 2) ? -1 : 1;
    return { position: position, vMode: vMode, leftSpace: leftSpace, rightSpace: rightSpace };
  }

  getFieldInfoArrowPosition(): string {
    let top: string = '0', left: string = '0', transform: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      switch(positionInfo.vMode) {
        case -1:
          top = (positionInfo.position.top - 11).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          transform = 'scaleY(-1)';
          break;
        case 0:
          top = (positionInfo.position.top + positionInfo.position.height / 2 - 4).toString() + 'px';
          if(positionInfo.rightSpace) {
            left = (positionInfo.position.right - 1).toString() + 'px';
            transform = 'rotate(-90deg)';
          } else {
            left = (positionInfo.position.left - 17).toString() + 'px';
            transform = 'rotate(90deg)';
          }
          break;
        case 1:
          top = (positionInfo.position.bottom + 4).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          break;
      }
    }
    return 'top:' + top + ';left:' + left + ';transform:' + transform + ';';
  }

  getFieldInfoPosition(): string {
    let top: string = '0', right: string = 'unset', bottom: string = 'unset', left: string = '0', maxHeight: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      if(positionInfo.vMode == 0) {
        maxHeight = (window.innerHeight - 20).toString() + 'px';
        const normalTop: number = positionInfo.position.top + positionInfo.position.height / 2 - 22;
        top = Math.min(Math.max(10, window.innerHeight - this.fieldInfo.nativeElement.offsetHeight - 10), normalTop).toString() + 'px';
        if(positionInfo.rightSpace) {
          left = (positionInfo.position.right + 11).toString() + 'px';
        } else {
          left = 'unset';
          right = (window.innerWidth - (positionInfo.position.left - 11)).toString() + 'px';
        }
      } else {
        const normalLeft: number = positionInfo.position.left + positionInfo.position.width / 2 - 22;
        const rightPos: number = positionInfo.position.right - positionInfo.position.width / 2 + 22;
        left = Math.min(Math.max(Math.max(10, rightPos - this.fieldInfo.nativeElement.offsetWidth), window.innerWidth - this.fieldInfo.nativeElement.offsetWidth - 10), normalLeft).toString() + 'px';
        if(positionInfo.vMode == -1) {
          top = 'unset';
          const bottomPos: number = positionInfo.position.top - 11;
          bottom = (window.innerHeight - bottomPos).toString() + 'px';
          maxHeight = (bottomPos - 10).toString() + 'px';
        } else {
          const topNum: number = positionInfo.position.bottom + 11;
          top = topNum.toString() + 'px';
          maxHeight = (window.innerHeight - topNum - 10).toString() + 'px';
        }
      }
    }
    return 'top:' + top + ';right:' + right + ';bottom:' + bottom + ';left:' + left + ';--max-height:' + maxHeight + ';';
  }

  showFieldInfo(event: any, text: string) {
    if(!this.fieldInfo)
      return;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[0].innerHTML = text;
    this.fieldInfoElem = event.target;
    this.fieldInfoShowing = true;
  }

  hideFieldInfo() {
    this.fieldInfoShowing = false;
    this.fieldInfoElem = null;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any): void {
    if(this.dialog.openDialogs && this.dialog.openDialogs.length > 0)
      return;
    if(this.fieldInfoShowing) {
      event.preventDefault();
      if(event.key == 'Escape')
        this.hideFieldInfo();
      return;
    }
    switch(event.key) {
      case 's':
        if((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey)) {
          event.preventDefault();
          this.getSelected()?.save();
        }
        break;
      case 'ArrowUp':
        if(!this.ignoreKeyboardArrowEvent(event)) {
          event.preventDefault();
          this.selectPrevious();
        }
        break;
      case 'ArrowDown':
        if(!(this.ignoreKeyboardArrowEvent(event))) {
          event.preventDefault();
          this.selectNext();
        }
        break;
      default:
    }
  }
}